import type { Moment } from 'moment';

import type {
  IWorkJourneyManagementUser,
  IWorkJourneyReplacementCandidate,
} from '@src/models/interfaces/workJourneyManagement.interface';
import type { JurisdictionIdentifier } from '@src/packages/utils/nationalId';
import type { ReplacementJourneyImpact } from '@src/packages/workJourney/utils/evaluateReplacementJourneyImpact';

export type TCoverReplaceDurationMode = 'single' | 'range' | 'permanent';

export type TCoverReplaceStep = 1 | 2;

export type CoverReplaceStep2Variant = 'replace' | 'assign';

export const getCoverReplaceStep2Variant = (
  impact: ReplacementJourneyImpact | null,
): CoverReplaceStep2Variant => (impact?.kind === 'impact' ? 'assign' : 'replace');

export type CoverReplaceModificationForm = {
  durationMode: TCoverReplaceDurationMode;
  startDate: Moment | null;
  endDate: Moment | null;
  /** Optional free-text note stored on the exception as `description` (trigger may copy to attendance observations). */
  comment: string;
};

export type CoverReplaceUserRow = {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  idCard: string;
  jurisdictionId?: JurisdictionIdentifier;
  address?: { country?: string | null } | null;
  photoURL: string | null;
  /** Display label (journey name or localized “no journey”). */
  currentJourneyLabel: string;
  /** False when `currentJourneyLabel` is the empty-state copy. */
  hasAssignedJourney: boolean;
};

export const toCoverReplaceUserRowFromCandidate = (
  user: IWorkJourneyReplacementCandidate,
  noJourneyLabel: string,
): CoverReplaceUserRow => {
  const journeyName = user.currentWorkJourney?.workJourneyConfigName?.trim() || '';
  return {
    id: user.id,
    displayName: user.displayName,
    firstName: user.firstName,
    lastName: user.lastName,
    idCard: user.idCard,
    jurisdictionId: user.jurisdictionId,
    address: user.address,
    photoURL: user.photoURL,
    currentJourneyLabel: journeyName || noJourneyLabel,
    hasAssignedJourney: Boolean(journeyName),
  };
};

export const toCoverReplaceUserRowFromManagementUser = (
  user: IWorkJourneyManagementUser,
  currentJourneyLabel: string,
  hasAssignedJourney = true,
): CoverReplaceUserRow => ({
  id: user.id,
  displayName: user.displayName,
  firstName: user.firstName,
  lastName: user.lastName,
  idCard: user.idCard,
  jurisdictionId: user.jurisdictionId,
  photoURL: null,
  currentJourneyLabel,
  hasAssignedJourney,
});
